import logger from 'utils/log';

/**
 * In-memory, tab-lifetime counters for session-sync health (AVS-4332).
 *
 * Nothing is sent anywhere — the numbers are read from the browser console through the separate
 * `utils/ui/syncTelemetryProbe` module. Counters are deliberately NOT cleared by AVS-4331's
 * `resetSessionSyncState`: a debugger wants the history across a session restart. Tests clear them
 * through `resetSyncTelemetry()` (wired into `utils/test/resetAppState`).
 *
 * ## Hard constraints (machine-checked by `syncTelemetry.test.ts`, not merely documented)
 *
 * 1. **This module imports nothing but `utils/log`.** `state/session` imports `sendApiRequest`
 *    from `utils/network`, and `utils/network` imports `userSession` from `state/session` — a real,
 *    verified cycle. `utils/network` imports THIS module, so any import of `state/`, `utils/tenant`,
 *    `utils/network` or `utils/state` here would close the loop. Anything needing the state layer
 *    (e.g. live listener counts) belongs in the probe module instead.
 * 2. **No `window` access.** This graph is evaluated in vitest-node and lambda contexts.
 * 3. **Every export is a `function` declaration, never a `const` arrow** — hoisting has to survive
 *    any residual module-cycle load ordering.
 * 4. **The clock is `Date.now()`, never `performance.now()`** — Vitest fakes `Date` by default but
 *    not necessarily `performance`, which would make every elapsed-time assertion read 0.
 * 5. **Counting is unsampled.** Only the high-volume stale-write *log emission* is throttled; a
 *    throttled log never drops a count.
 * 6. **No PII.** Numbers, booleans and nulls only — no ids, tokens, session payloads or URLs.
 */

/** The two independent sockets. One merged number could not say which channel died. */
export type TSyncSocket = 'broadcast' | 'voip';

/** First stale write logs immediately, then at most one log per window. Counting is unaffected. */
export const LOG_THROTTLE_MS = 60_000;

interface ISyncTelemetryCounters {
  staleWrites: number;
  lastStaleWriteAt: number | null;
  socketReconnectAttempts: Record<TSyncSocket, number>;
  socketGiveUps: Record<TSyncSocket, number>;
  lastGiveUpAt: Record<TSyncSocket, number | null>;
  degradedEntries: number;
  degradedExits: number;
  degradedTotalMs: number;
  degradedSince: number | null;
  guardTrips: number;
}

export interface ISyncTelemetrySnapshot extends ISyncTelemetryCounters {
  /** Convenience roll-ups so a console read does not have to add the per-socket numbers up. */
  socketReconnectAttemptsTotal: number;
  socketGiveUpsTotal: number;
  /** `true` exactly when `degradedSince !== null`. */
  degraded: boolean;
}

function createCounters(): ISyncTelemetryCounters {
  return {
    staleWrites: 0,
    lastStaleWriteAt: null,
    socketReconnectAttempts: { broadcast: 0, voip: 0 },
    socketGiveUps: { broadcast: 0, voip: 0 },
    lastGiveUpAt: { broadcast: null, voip: null },
    degradedEntries: 0,
    degradedExits: 0,
    degradedTotalMs: 0,
    degradedSince: null,
    guardTrips: 0,
  };
}

let counters: ISyncTelemetryCounters = createCounters();

/** Timestamp of the last emitted stale-write log line — throttle bookkeeping only. */
let lastStaleWriteLogAt: number | null = null;

/**
 * A write was rejected as stale (HTTP 409). Recorded transport-level, so it is independent of
 * whether the caller opted into the typed throw.
 *
 * The count increments on EVERY call; only the log line is throttled.
 *
 * `logger.info` (not `warn`/`error`) is deliberate: several suites hand-mock `utils/log` with just
 * `{ log, info }` (e.g. `utils/ui/rtc.test.ts`), so anything else would throw inside them.
 */
export function recordStaleWrite(): void {
  const now = Date.now();
  counters.staleWrites += 1;
  counters.lastStaleWriteAt = now;

  const throttled = lastStaleWriteLogAt !== null && now - lastStaleWriteLogAt < LOG_THROTTLE_MS;
  if (throttled) {
    return;
  }

  lastStaleWriteLogAt = now;
  logger.info(`[SyncTelemetry] stale write rejected — total ${counters.staleWrites}`);
}

/**
 * A socket reconnect was *scheduled*. Measures scheduled attempts, not executed ones: a
 * `disconnect()` can cancel a pending timer so the attempt never runs.
 */
export function recordSocketReconnectAttempt(source: TSyncSocket): void {
  counters.socketReconnectAttempts[source] += 1;
}

/** A socket exhausted its reconnect budget and gave up. */
export function recordSocketGaveUp(source: TSyncSocket): void {
  counters.socketGiveUps[source] += 1;
  counters.lastGiveUpAt[source] = Date.now();
}

/**
 * Entered degraded session-sync mode. Idempotent: a second enter without an intervening exit
 * neither double-counts nor moves `degradedSince`, so the accumulated duration stays honest.
 */
export function recordDegradedEnter(): void {
  if (counters.degradedSince !== null) {
    return;
  }

  counters.degradedSince = Date.now();
  counters.degradedEntries += 1;
}

/**
 * Left degraded session-sync mode. An exit with no matching enter is a no-op — otherwise the
 * unconditional `stopSyncDegradedPoll()` on every healthy connect would accumulate NaN.
 *
 * Invariant, both here and in `recordDegradedEnter`: `degradedSince === null` exactly when not
 * degraded.
 */
export function recordDegradedExit(): void {
  const since = counters.degradedSince;
  if (since === null) {
    return;
  }

  counters.degradedTotalMs += Date.now() - since;
  counters.degradedExits += 1;
  counters.degradedSince = null;
}

/** The `EventEmitter` listener-growth guard tripped (wired by the guard task). */
export function recordListenerGuardTrip(): void {
  counters.guardTrips += 1;
}

/** A deep copy — mutating the result cannot corrupt the internal counters. */
export function syncTelemetrySnapshot(): ISyncTelemetrySnapshot {
  return {
    staleWrites: counters.staleWrites,
    lastStaleWriteAt: counters.lastStaleWriteAt,
    socketReconnectAttempts: { ...counters.socketReconnectAttempts },
    socketGiveUps: { ...counters.socketGiveUps },
    lastGiveUpAt: { ...counters.lastGiveUpAt },
    socketReconnectAttemptsTotal: counters.socketReconnectAttempts.broadcast + counters.socketReconnectAttempts.voip,
    socketGiveUpsTotal: counters.socketGiveUps.broadcast + counters.socketGiveUps.voip,
    degradedEntries: counters.degradedEntries,
    degradedExits: counters.degradedExits,
    degradedTotalMs: counters.degradedTotalMs,
    degradedSince: counters.degradedSince,
    degraded: counters.degradedSince !== null,
    guardTrips: counters.guardTrips,
  };
}

/** Test-only reset (see `utils/test/resetAppState`). Also re-arms the log throttle. */
export function resetSyncTelemetry(): void {
  counters = createCounters();
  lastStaleWriteLogAt = null;
}
